import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const FeatureAccessContext = createContext({});

// No answer yet. A token of `null` is a real value (a cookie-only session),
// so "nothing loaded" needs a value of its own.
const NOT_LOADED = Symbol('not loaded');

export function FeatureAccessProvider({ children }) {
  const { api, user, loading: authLoading, getToken, addSignInGate } = useAuth();
  const [features, setFeatures] = useState({});
  const [userPlan, setUserPlan] = useState('free');
  // The credential the answer in `features` was fetched with. Every sign-in
  // gets a new token, so an answer fetched for one sign-in is never taken for
  // the next one's.
  const [loadedToken, setLoadedToken] = useState(NOT_LOADED);
  const intervalRef = useRef(null);
  // A request sent before `user` was known: { token, promise of "it worked" }.
  const earlyRef = useRef(null);
  const askedAtStartRef = useRef(false);

  const fetchAccess = useCallback(async ({ early = false } = {}) => {
    const token = getToken();
    try {
      const res = await api.get('/feature-access');
      setFeatures(res.data.features || {});
      setUserPlan(res.data.user_plan || 'free');
      setLoadedToken(token);
      return true;
    } catch (err) {
      console.error('Failed to load feature access:', err);
      // A signed-in person is let through on a failed load, as before (the
      // server decides on every request anyway). An early request that fails
      // settles nothing: the request sent once `user` is known does.
      if (!early) setLoadedToken(token);
      return false;
    }
  }, [api, getToken]);

  // The signed-in app is not drawn until this answer is in (see `ready`
  // below), and it used to be asked for only once `user` was known: one more
  // round trip with nothing on screen, after the sign-in check on a page
  // load and after the sign-in form on a sign-in. Both waits are gone when
  // the question is put as soon as there is a token to put it with.
  const askEarly = useCallback(() => {
    const promise = fetchAccess({ early: true });
    earlyRef.current = { token: getToken(), promise };
    return promise;
  }, [fetchAccess, getToken]);

  // Page load with a stored token: alongside the sign-in check.
  useEffect(() => {
    if (askedAtStartRef.current) return;
    askedAtStartRef.current = true;
    if (authLoading && !user && getToken()) askEarly();
    // Mount only: this is about the sign-in check that is in flight right now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sign-in: before the person is shown as signed in (AuthContext holds the
  // sign-in until this has answered, or for a few seconds at most).
  useEffect(() => addSignInGate(askEarly), [addSignInGate, askEarly]);

  const loadAccess = useCallback(async () => {
    if (!user) return;
    // The early answer, if it is for this sign-in and it worked, is the
    // answer: do not ask the same thing twice in a row.
    const early = earlyRef.current;
    earlyRef.current = null;
    if (early && early.token === getToken() && (await early.promise)) return;
    await fetchAccess();
  }, [user, fetchAccess, getToken]);

  useEffect(() => { loadAccess(); }, [loadAccess]);

  // Poll every 30 seconds to pick up admin changes without page refresh
  useEffect(() => {
    if (!user) return;
    intervalRef.current = setInterval(loadAccess, 30000);
    return () => clearInterval(intervalRef.current);
  }, [user, loadAccess]);

  const canAccess = useCallback((featureKey) => {
    const f = features[featureKey];
    if (!f) return { accessible: true, reason: 'not_gated' };
    return f;
  }, [features]);

  // Loaded = there is an answer and it is not some other sign-in's. A token
  // that has just been cleared (an expired session on its way to the sign-in
  // page) does not take the answer back: the page stays as it is until the
  // browser has left it.
  const token = getToken();
  const loaded = loadedToken !== NOT_LOADED && (!token || loadedToken === token);

  // Don't render children until feature access is resolved.
  // If there's no user, loaded never flips (loadAccess returns early) —
  // so treat no-user as immediately ready.
  const ready = loaded || !user;

  return (
    <FeatureAccessContext.Provider value={{ features, userPlan, loaded, canAccess, reload: loadAccess }}>
      {ready ? children : null}
    </FeatureAccessContext.Provider>
  );
}

export function useFeatureAccess() {
  return useContext(FeatureAccessContext);
}
